import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Paper,
    TableBody,
    TableRow,
    TableCell,
    TableHead,
    Table,
    Button,
    IconButton,
    Tooltip,
    TextField,
    Snackbar,
    Alert,
    Box,
    Typography,
    Chip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import RoundedCornerIcon from '@mui/icons-material/RoundedCorner';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import CustomDatePicker from '../../Widgets/CustomDates/CustomDatePicker/CustomDatePicker';
import Iframe from 'react-iframe';
import NoDocumentsPdf from '../../../assests/pdf/NoDocument.pdf';
import Autocomplete from '@mui/material/Autocomplete';   // ⬅️ NEW
import Constants from '../../../assests/Constants/constants.js';   // ⬅️ NEW
import {
    formatLinkAmount,
    getDocumentAvailableAmount,
    getStatusClassName,
    getStatusLabelKey,
    projectGenerateAllocation,
    roundAmount,
    TAX_INVOICE_TYPE,
} from '../documentLifecycle.js';
const StyledContainer = styled('div')({
    position: 'absolute',
    top: '6%',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'white',
    borderRadius: '8px',
    padding: '24px',
    maxHeight: '88vh',
    overflowY: 'auto',
    width: '85%',
    zIndex: 1300,
    boxShadow: '0px 4px 20px rgba(0,0,0,0.3)',
    border: '1px solid #304fff'
});

const StyledTableContainer = styled(Paper)({
    marginTop: '10px',
    overflowX: 'auto'
});

const StyledTable = styled(Table)({
    minWidth: 800
});

const StyledTableCellHeader = styled(TableCell)({
    fontWeight: 'bold',
    backgroundColor: '#f5f5f5',
    textAlign: 'center'
});

const getSourceDocumentsLabelKey = (sourceDocs = []) => {
    if (!sourceDocs.length) return 'SourceDocuments';
    const sourceType = Number(sourceDocs[0]?.transactionDocumentType);
    if (sourceType === 1) return 'SourceQuotations';
    if (sourceType === 2) return 'SourceOrders';
    if (sourceType === 3) return 'SourceTransactionAccounts';
    if (sourceType === 4) return 'SourceInvoices';
    if (sourceType === 11) return 'SourceDeliveryNotes';
    return 'SourceDocuments';
};

/** ------------------------------------------------------------------ */
/**                    Receipt-Items Modal (full)                      */
/** ------------------------------------------------------------------ */
const ReceiptItemsModal = ({ open, onClose, onSave, doc, pendingAction }) => {
    const { t, i18n } = useTranslation();
    const [direction, setDirection] = useState('rtl');
    const [previewIndex, setPreviewIndex] = useState(0);

    /* ------------------------------------------------------------------ */
    /*                         i18n direction                             */
    /* ------------------------------------------------------------------ */
    useEffect(() => {
        setDirection(['he', 'ar'].includes(i18n.language) ? 'rtl' : 'ltr');
    }, [i18n.language]);

    useEffect(() => {
        if (open) setPreviewIndex(0);
    }, [open, doc?.id]);

    /**
     * Target amount to match with payment rows. Prefer the amount chosen in GenerateDocumentModal
     * (remaining of a partly paid TaxInvoice); fall back to lifecycle remaining / source totals.
     */
    const documentTargetAmount = useMemo(() => {
        if (doc?.amount != null && !Number.isNaN(Number(doc.amount))) {
            return roundAmount(Number(doc.amount) || 0);
        }
        const available = getDocumentAvailableAmount(doc);
        if (available > 0) return available;
        const sources = doc?.sourceDocuments;
        if (Array.isArray(sources) && sources.length > 1) {
            return roundAmount(
                sources.reduce((sum, d) => sum + getDocumentAvailableAmount(d), 0)
            );
        }
        return 0;
    }, [doc]);

    const sourceAvailableAmount = useMemo(() => {
        const fromDoc = doc?.remainingAmount ?? doc?.RemainingAmount;
        if (fromDoc != null && !Number.isNaN(Number(fromDoc))) {
            return roundAmount(Math.max(0, Number(fromDoc)));
        }
        return getDocumentAvailableAmount(doc) || documentTargetAmount;
    }, [doc, documentTargetAmount]);

    const showAllocationSummary = Number(doc?.transactionDocumentType) === TAX_INVOICE_TYPE
        || (doc?.remainingAmount != null && Number(doc.remainingAmount) >= 0)
        || (doc?.originalAmount != null
            && Math.abs(Number(doc.originalAmount) - documentTargetAmount) > 0.01);

    const previewDocs = useMemo(() => {
        if (doc?.sourceDocuments?.length > 1) return doc.sourceDocuments;
        return doc ? [doc] : [];
    }, [doc]);

    const isMultiSource = previewDocs.length > 1;
    const sourceDocumentsLabelKey = useMemo(
        () => getSourceDocumentsLabelKey(previewDocs),
        [previewDocs]
    );
    const currentPreviewDoc = previewDocs[previewIndex] ?? previewDocs[0] ?? doc;
    const previewUrl = currentPreviewDoc?.documentUrl
        ? `${currentPreviewDoc.documentUrl}#page=1`
        : `${NoDocumentsPdf}#page=1`;
    const hasPreview = previewDocs.some((d) => d?.documentUrl);

    /* ------------------------------------------------------------------ */
    /*                       receipt-items state                          */
    /* ------------------------------------------------------------------ */
    const [checks, setChecks] = useState([]);
    const [creditCards, setCreditCards] = useState([]);
    const [cash, setCash] = useState([]);
    const [bankTransactions, setBankTransfers] = useState([]);
    const [offsets, setOffsets] = useState([]);
    const [withholdingTaxRowAdded, setWithholdingTaxRowAdded] = useState(false);
    const [withholdingTax, setWithholdingTax] = useState('');
    /**
     * Cumulative rounding adjustment applied to the invoice side.
     * When non-zero, an extra "Rounding Difference" line item with this price
     * (and includeVat=false) is appended to the invoice items on save, so that
     *   invoiceTotal + roundingAdjustment === receiptTotal (sum + withholding).
     * Only ever set via the Round button, which caps it to ±1 shekel.
     */
    const [roundingAdjustment, setRoundingAdjustment] = useState(0);
    /* ------------------------------------------------------------------ */
    /*                    running total + error logic                     */
    /* ------------------------------------------------------------------ */
    const [total, setTotal] = useState(0);
    const [errorText, setErrorText] = useState('');
    const [errorExceedsAmount, setErrorExceedsAmount] = useState(false);
    /** recompute total + mismatch check */
    useEffect(() => {
        const sum = [
            ...checks,
            ...creditCards,
            ...cash,
            ...bankTransactions,
            ...offsets
        ].reduce((acc, r) => acc + (+r.amount || 0), 0) + (+withholdingTax || 0); // <-- add withholdingTax here

        setTotal(sum);

        // Effective invoice total takes the user-applied rounding adjustment
        // into account so the receipt side can match without an exceeds error.
        const effectiveDocAmount = documentTargetAmount + (Number(roundingAdjustment) || 0);

        if (doc != null && Math.abs(sum - effectiveDocAmount) > 0.01) {
            setErrorText(t('TotalItemsAmountExceedsDocumentAmount'));
            setErrorExceedsAmount(true);
        } else {
            setErrorText('');
            setErrorExceedsAmount(false);
        }
    }, [checks, creditCards, cash, bankTransactions, offsets, withholdingTax, doc, documentTargetAmount, t, roundingAdjustment]);

    /** Live projection: payments entered so far vs what is still free on the TaxInvoice. */
    const allocationProjection = useMemo(
        () => projectGenerateAllocation(sourceAvailableAmount, total > 0 ? total : documentTargetAmount),
        [sourceAvailableAmount, total, documentTargetAmount]
    );

    /* ------------------------------------------------------------------ */
    /*                          helpers                                   */
    /* ------------------------------------------------------------------ */
    const addReceiptItem = type => {
        const blank = {
            amount: '',
            paymentDate: '',
            description: '',
            bankName: '',
            bankCode: '',
            bankBranchCode: '',
            bankAccountNumber: ''
        };
        switch (type) {
            case 'checks': setChecks(prev => [...prev, blank]); break;
            case 'creditCards': setCreditCards(prev => [...prev, blank]); break;
            case 'cash': setCash(prev => [...prev, blank]); break;
            case 'bankTransactions': setBankTransfers(prev => [...prev, blank]); break;
            case 'offsets': setOffsets(prev => [...prev, blank]); break;
        }
    };

    const removeItem = (type, idx) => {
        const filterOut = setter =>
            setter(prev => prev.filter((_, i) => i !== idx));

        switch (type) {
            case 'checks': filterOut(setChecks); break;
            case 'creditCards': filterOut(setCreditCards); break;
            case 'cash': filterOut(setCash); break;
            case 'bankTransactions': filterOut(setBankTransfers); break;
            case 'offsets': filterOut(setOffsets); break;
        }
    };

    const onFieldChange = (type, idx, field, value) => {
        const update = setter =>
            setter(prev => {
                const copy = [...prev];
                copy[idx] = { ...copy[idx], [field]: value };
                return copy;
            });

        switch (type) {
            case 'checks': update(setChecks); break;
            case 'creditCards': update(setCreditCards); break;
            case 'cash': update(setCash); break;
            case 'bankTransactions': update(setBankTransfers); break;
            case 'offsets': update(setOffsets); break;
        }
    };

    /* ------------------------------------------------------------------ */
    /*                    table row renderer                              */
    /* ------------------------------------------------------------------ */
    const rows = [
        ...checks.map((r, i) => ({ ...r, type: 'checks', idx: i })),
        ...creditCards.map((r, i) => ({ ...r, type: 'creditCards', idx: i })),
        ...cash.map((r, i) => ({ ...r, type: 'cash', idx: i })),
        ...bankTransactions.map((r, i) => ({ ...r, type: 'bankTransactions', idx: i })),
        ...offsets.map((r, i) => ({ ...r, type: 'offsets', idx: i }))
    ];

    const inputProps = {
        disableUnderline: true,
        sx: {
            height: '40px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: '1px solid transparent',
            '&:focus-within': { border: '2px solid #304FFF' }
        }
    };

    const textAlignProp = {
        style: { textAlign: direction === 'ltr' ? 'left' : 'right' }
    };

    const boxedInput = (hasError = false) => ({
        disableUnderline: true,
        sx: {
            height: '40px',
            padding: '0 10px',
            borderRadius: '8px',
            background: '#F5F5F6',
            border: `2px solid ${hasError ? '#d32f2f' : 'transparent'}`,
            '&:focus-within': {
                border: `2px solid ${hasError ? '#d32f2f' : '#304FFF'}`,
            },
        },
    });

    const receiptItemsValid = useMemo(() => {
        const buckets = { checks, creditCards, cash, bankTransactions, offsets };

        /* require at least one row OR withholding tax */
        const hasRows = Object.values(buckets).some(arr => arr.length > 0);
        const hasWithholdingTax = withholdingTaxRowAdded && withholdingTax && Number(withholdingTax) > 0;
        
        if (!hasRows && !hasWithholdingTax) return false;

        const eachItemOk = (arr) =>
            arr.every(item =>
                item.amount &&
                item.amount !== null &&
                item.amount !== undefined &&
                item.paymentDate &&
                item.paymentDate !== null &&
                item.paymentDate !== undefined
            );

        return (
            eachItemOk(checks) &&
            eachItemOk(creditCards) &&
            eachItemOk(cash) &&
            eachItemOk(bankTransactions) &&
            eachItemOk(offsets)
        );
    }, [checks, creditCards, cash, bankTransactions, offsets, withholdingTaxRowAdded, withholdingTax]);


    /* ------------------------------------------------------------------ */
    /*                       rounding helpers                             */
    /* ------------------------------------------------------------------ */
    // Drop any stale rounding adjustment when the modal closes so a fresh
    // open starts clean (other receipt-item state already persists between
    // opens, which is pre-existing behavior).
    useEffect(() => {
        if (!open) setRoundingAdjustment(0);
    }, [open]);

    /**
     * Only enable rounding for invoice-receipt generation: that's the only
     * flow where a synthetic invoice line can be appended to balance the
     * receipt. Plain receipts (no invoice items) and cancel flows are out.
     */
    const isInvoiceReceiptGeneration = pendingAction?.value === 'generate_invoice_receipt';

    /**
     * Current visible delta = receiptTotal − effectiveInvoiceTotal.
     * Negative means the user typed payments below the invoice; the rounding
     * line we'd add to the invoice would have a negative price.
     */
    const roundingDelta = useMemo(() => {
        if (doc == null) return 0;
        const effectiveDocAmount = documentTargetAmount + (Number(roundingAdjustment) || 0);
        return Number((total - effectiveDocAmount).toFixed(2));
    }, [total, doc, documentTargetAmount, roundingAdjustment]);

    const canRound = isInvoiceReceiptGeneration
        && Math.abs(roundingDelta) > 0.005
        && Math.abs(roundingDelta) <= 1;

    const handleRound = () => {
        if (!canRound) return;
        // Set adjustment so invoice total + adjustment exactly matches receipt total.
        const newAdjustment = Number((total - documentTargetAmount).toFixed(2));
        setRoundingAdjustment(newAdjustment);
    };

    /* ------------------------------------------------------------------ */
    /*                         save handler                               */
    /* ------------------------------------------------------------------ */
    const handleSubmit = () => {
        const receiptItems = { checks, creditCards, cash, bankTransactions, offsets };

        const hasReceiptItems = Object.values(receiptItems)
            .some(arr => arr && arr.length > 0);
        const hasWithholdingTax = withholdingTaxRowAdded && withholdingTax && Number(withholdingTax) > 0;

        if (!hasReceiptItems && !hasWithholdingTax) {
            setErrorText(t('PleaseAddAtLeastOneReceiptItem'));
            return;
        }


        /* 🔐 any row missing amount or date? */
        if (!receiptItemsValid) {
            setErrorText(t('ReceiptItemMissingFields'));
            return;
        }

        if (errorExceedsAmount) {
            setErrorText(t('TotalItemsAmountExceedsDocumentAmount'));
            return;
        }

        onSave(receiptItems, withholdingTax, doc, Number(roundingAdjustment) || 0);
    };

    if (!open) return null;

    return (
        <Modal open={open} onClose={onClose}>
            <StyledContainer dir={direction}>
                <h2 style={{ margin: 0, textAlign: direction === 'ltr' ? 'left' : 'right' }}>
                    {t('ReceiptItems')}
                </h2>
                {hasPreview && (
                    <StyledTableContainer sx={{ mt: 3, p: 0, overflow: 'hidden' }}>
                        {isMultiSource && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    px: 1.5,
                                    py: 1,
                                    borderBottom: '1px solid #E0E0E0',
                                    backgroundColor: '#F5F7FF',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="body2" fontWeight={600}>
                                        {t(sourceDocumentsLabelKey)}
                                    </Typography>
                                    {previewDocs.map((sourceDoc, index) => (
                                        <Chip
                                            key={sourceDoc.id ?? index}
                                            size="small"
                                            label={sourceDoc.reference || index + 1}
                                            color={previewIndex === index ? 'primary' : 'default'}
                                            variant={previewIndex === index ? 'filled' : 'outlined'}
                                            onClick={() => setPreviewIndex(index)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        disabled={previewIndex <= 0}
                                        onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
                                    >
                                        <ArrowBackIcon sx={{ color: '#304FFF' }} />
                                    </IconButton>
                                    <Typography variant="caption">
                                        {previewIndex + 1} / {previewDocs.length}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={previewIndex >= previewDocs.length - 1}
                                        onClick={() => setPreviewIndex((i) => Math.min(previewDocs.length - 1, i + 1))}
                                    >
                                        <ArrowForwardIcon sx={{ color: '#304FFF' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                        <Iframe
                            url={previewUrl}
                            width="100%"
                            height="400px"
                            style={{ border: 'none' }}
                            title={isMultiSource
                                ? `${t(sourceDocumentsLabelKey)} ${currentPreviewDoc?.reference || previewIndex + 1}`
                                : 'PDF Preview'}
                        />
                    </StyledTableContainer>
                )}

                {showAllocationSummary && (
                    <Box
                        dir={direction}
                        sx={{
                            mt: 1.5,
                            p: 1.5,
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 3,
                            alignItems: 'flex-start',
                            border: '1px solid #E5E7F6',
                            borderRadius: 2,
                            backgroundColor: '#F8F9FF',
                        }}
                    >
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('LinkDocumentsAvailableAmount')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatLinkAmount(sourceAvailableAmount)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('LinkDocumentsTotalToLink')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatLinkAmount(documentTargetAmount)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('LinkDocumentsRemainingAfter')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatLinkAmount(allocationProjection.remainingAfter)}
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t('LinkDocumentsResultingStatus')}
                            </Typography>
                            <Chip
                                size="small"
                                variant="outlined"
                                color={
                                    getStatusClassName({
                                        processingStatus: allocationProjection.sourceStatus,
                                        transactionDocumentType: TAX_INVOICE_TYPE,
                                    }) === 'processed'
                                        ? 'success'
                                        : 'warning'
                                }
                                label={t(getStatusLabelKey({
                                    processingStatus: allocationProjection.sourceStatus,
                                    transactionDocumentType: TAX_INVOICE_TYPE,
                                }))}
                            />
                        </Box>
                    </Box>
                )}

                {/* quick-add buttons */}
                <div style={{ display: 'flex', gap: 10, margin: '14px 0', flexWrap: 'wrap' }}>
                    <Button onClick={() => addReceiptItem('checks')} sx={btnStyle}>{t('AddCheck')}</Button>

                    {pendingAction?.value !== 'generate_receipt_deferred' && (
                        <>
                            <Button onClick={() => addReceiptItem('creditCards')} sx={btnStyle}>{t('AddCreditCard')}</Button>
                            <Button onClick={() => addReceiptItem('cash')} sx={btnStyle}>{t('AddCash')}</Button>
                            <Button onClick={() => addReceiptItem('bankTransactions')} sx={btnStyle}>{t('AddBankTransfer')}</Button>
                            <Button onClick={() => addReceiptItem('offsets')} sx={btnStyle}>{t('AddOffset')}</Button>
                        </>
                    )}
                    <Button
                        onClick={() => setWithholdingTaxRowAdded(true)}
                        disabled={withholdingTaxRowAdded}
                        sx={{
                            background: withholdingTaxRowAdded ? '#aaa' : '#304FFF',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            '&:hover': {
                                background: withholdingTaxRowAdded ? '#aaa' : '#1E40FF',
                            }
                        }}
                    >
                        {t('AddWithholdingTax')}
                    </Button>
                    {isInvoiceReceiptGeneration && (
                        <Tooltip
                            title={canRound
                                ? t('RoundReceiptTooltip')
                                : t('RoundReceiptDisabledTooltip')}
                        >
                            <span>
                                <Button
                                    onClick={handleRound}
                                    disabled={!canRound}
                                    startIcon={<RoundedCornerIcon />}
                                    sx={{
                                        background: canRound ? '#304FFF' : '#aaa',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        '&:hover': {
                                            background: canRound ? '#1E40FF' : '#aaa',
                                        },
                                        '&.Mui-disabled': {
                                            background: '#aaa',
                                            color: '#fff',
                                            opacity: 0.6,
                                        },
                                    }}
                                >
                                    {t('RoundReceipt')}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </div>

                {/* table */}
                <StyledTableContainer>
                    <StyledTable stickyHeader>
                        <TableHead>
                            <TableRow>
                                {['Type', 'CheckNumberDescription', 'BankName', 'BankCode',
                                    'BankBranchCode', 'supplierAccountNumber', 'Amount',
                                    'DuePaymentDate', 'Actions'].map(label => (
                                        <StyledTableCellHeader key={label}>{t(label)}</StyledTableCellHeader>
                                    ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {rows.map(r => {
                                const isCheck = r.type === 'checks';

                                return (
                                    <TableRow key={`${r.type}-${r.idx}`}>
                                        {/* ──────────── TYPE ──────────── */}
                                        <TableCell align="center">{t(r.type)}</TableCell>

                                        {/* ───── DESCRIPTION / CHECK-NUMBER ───── */}
                                        <TableCell align="center">
                                            <TextField
                                                placeholder={t(isCheck ? 'CheckNumber' : 'Description')}
                                                value={r.description || ''}
                                                onChange={e =>
                                                    onFieldChange(r.type, r.idx, 'description', e.target.value)
                                                }
                                                variant="standard"
                                                fullWidth
                                                InputProps={inputProps}
                                                inputProps={textAlignProp}
                                            />
                                        </TableCell>

                                        {/* ────── BANK-NAME  (checks only) ────── */}
                                        <TableCell align="center">
                                            {isCheck ? (
                                                <Autocomplete
                                                    disablePortal
                                                    size="small"
                                                    options={Constants.ISRAEL_BANKS}
                                                    getOptionLabel={(o) => `${o.code} – ${o.name}`}
                                                    value={
                                                        Constants.ISRAEL_BANKS.find(
                                                            (b) => b.code === r.bankCode || b.name === r.bankName
                                                        ) || null
                                                    }
                                                    onChange={(_, newVal) => {
                                                        const newName = newVal ? newVal.name : '';
                                                        const newCode = newVal ? newVal.code : '';
                                                        /* update both bankName + bankCode */
                                                        onFieldChange('checks', r.idx, 'bankName', newName);
                                                        onFieldChange('checks', r.idx, 'bankCode', newCode);
                                                    }}
                                                    renderInput={(params) => (
                                                        <TextField
                                                            {...params}
                                                            placeholder={t('BankName')}
                                                            variant="standard"
                                                            InputProps={{
                                                                ...params.InputProps,
                                                                disableUnderline: true,
                                                                className: `custom-box-sizing-border-box ${direction}`,
                                                                sx: {
                                                                    height: '40px',
                                                                    padding: '0 10px',
                                                                    borderRadius: '8px',
                                                                    background: '#F5F5F6',
                                                                    border: '1px solid transparent',
                                                                    '&:focus-within': { border: '2px solid #304FFF' },
                                                                },
                                                            }}
                                                            inputProps={{
                                                                ...params.inputProps,
                                                                style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                            }}
                                                        />
                                                    )}
                                                    isOptionEqualToValue={(o, v) => o.code === v.code}
                                                />
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </TableCell>

                                        {/* ────── BANK-CODE  (read-only number) ────── */}
                                        <TableCell align="center">
                                            {isCheck ? (
                                                <TextField
                                                    fullWidth
                                                    type="number"
                                                    placeholder={t('BankCode')}
                                                    value={r.bankCode || ''}
                                                    variant="standard"
                                                    InputProps={{
                                                        disableUnderline: true,
                                                        className: `custom-box-sizing-border-box ${direction}`,
                                                        sx: {
                                                            height: '40px',
                                                            padding: '0 10px',
                                                            borderRadius: '8px',
                                                            background: '#F5F5F6',
                                                            border: '1px solid transparent',
                                                        },
                                                    }}
                                                    inputProps={{
                                                        style: { textAlign: direction === 'ltr' ? 'left' : 'right' },
                                                        readOnly: true,
                                                    }}
                                                />
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </TableCell>

                                        {/* ────── BANK-BRANCH-CODE ────── */}
                                        <TableCell align="center">
                                            {isCheck ? (
                                                <TextField
                                                    type="number"
                                                    placeholder={t('bankBranchCode')}
                                                    value={r.bankBranchCode || ''}
                                                    onChange={e =>
                                                        onFieldChange(r.type, r.idx, 'bankBranchCode', e.target.value)
                                                    }
                                                    variant="standard"
                                                    fullWidth
                                                    InputProps={inputProps}
                                                    inputProps={textAlignProp}
                                                />
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </TableCell>

                                        {/* ────── SUPPLIER-ACCOUNT-NUMBER ────── */}
                                        <TableCell align="center">
                                            {isCheck ? (
                                                <TextField
                                                    type="number"
                                                    placeholder={t('supplierAccountNumber')}
                                                    value={r.supplierAccountNumber || ''}
                                                    onChange={e =>
                                                        onFieldChange(
                                                            r.type,
                                                            r.idx,
                                                            'supplierAccountNumber',
                                                            e.target.value
                                                        )
                                                    }
                                                    variant="standard"
                                                    fullWidth
                                                    InputProps={inputProps}
                                                    inputProps={textAlignProp}
                                                />
                                            ) : (
                                                <span>-</span>
                                            )}
                                        </TableCell>

                                        {/* ────────── AMOUNT ────────── */}
                                        <TableCell align="center">
                                            <TextField
                                                type="number"
                                                placeholder={t('Amount')}
                                                value={r.amount || ''}
                                                onChange={e => onFieldChange(r.type, r.idx, 'amount', e.target.value)}
                                                variant="standard"
                                                fullWidth
                                                InputProps={boxedInput(!(Number(r.amount) > 0))}
                                                inputProps={{ ...textAlignProp, onWheel: (e) => e.target.blur() }}
                                            />
                                        </TableCell>

                                        {/* ────────── PAYMENT-DATE ────────── */}
                                        <TableCell align="center">
                                            <CustomDatePicker
                                                date={r.paymentDate ? r.paymentDate : null}
                                                onDateChange={date =>
                                                    onFieldChange(r.type, r.idx, 'paymentDate', date)
                                                }
                                                height="40px"
                                                error={!r.paymentDate}
                                            />
                                        </TableCell>

                                        {/* ────────── DELETE ────────── */}
                                        <TableCell align="center">
                                            <Tooltip title={t('Delete')}>
                                                <IconButton onClick={() => removeItem(r.type, r.idx)}>
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}

                            {withholdingTaxRowAdded && (
                                <TableRow>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                        {t('WithholdingTax')}
                                    </TableCell>
                                    <TableCell colSpan={5} />
                                    <TableCell colSpan={2} align="right">
                                        <TextField
                                            fullWidth
                                            placeholder={t('WithholdingTax')}
                                            type="number"
                                            value={withholdingTax}
                                            onChange={(e) => setWithholdingTax(e.target.value)}
                                            variant="standard"
                                            InputProps={{
                                                disableUnderline: true,
                                                sx: {
                                                    height: '40px',
                                                    padding: '0 10px',
                                                    borderRadius: '8px',
                                                    background: '#F5F5F6',
                                                    border: '1px solid transparent',
                                                    '&:focus-within': {
                                                        border: '2px solid #304FFF',
                                                    },
                                                },
                                            }}
                                            inputProps={{
                                                style: {
                                                    textAlign: direction === 'ltr' ? 'left' : 'right',
                                                },
                                                onWheel: (e) => e.target.blur(),
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Button
                                            onClick={() => {
                                                setWithholdingTaxRowAdded(false);
                                                setWithholdingTax('');
                                            }}
                                            style={{ color: 'red', minWidth: '20px' }}
                                        >
                                            <DeleteIcon />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </StyledTable>
                </StyledTableContainer>

                {/* total summary */}
                {doc && (
                    <div style={{ marginTop: 12, fontWeight: 600 }}>
                        {t('Total')}:&nbsp;
                        <span style={{ color: errorText ? '#d32f2f' : '#1976d2' }}>
                            {total.toFixed(2)}
                        </span>
                        &nbsp;/&nbsp;
                        {(documentTargetAmount + (Number(roundingAdjustment) || 0)).toFixed(2)}
                        {withholdingTaxRowAdded && withholdingTax && Number(withholdingTax) > 0 && (
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px' }}>
                                {t('ReceiptItems')}: {(total - Number(withholdingTax)).toFixed(2)} + {t('WithholdingTax')}: {Number(withholdingTax).toFixed(2)}
                            </div>
                        )}
                        {Math.abs(Number(roundingAdjustment) || 0) >= 0.005 && (
                            <div style={{ fontSize: '0.9em', color: '#666', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span>
                                    {t('RoundingDifference')}: {Number(roundingAdjustment).toFixed(2)}
                                </span>
                                <Button
                                    size="small"
                                    onClick={() => setRoundingAdjustment(0)}
                                    sx={{ color: '#d32f2f', minWidth: 'unset', padding: '0 6px' }}
                                >
                                    {t('Clear')}
                                </Button>
                            </div>
                        )}
                    </div>
                )}

                {/* PDF preview */}


                {/* footer buttons */}
                <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <Button variant="outlined" onClick={onClose}>{t('Cancel')}</Button>
                    <Button
                        variant="contained"
                        sx={{ background: '#304FFF' }}
                        onClick={handleSubmit}
                        disabled={!!errorText || !receiptItemsValid}
                    >
                        {t('Save')}
                    </Button>
                </div>

                {/* dynamic snackbar */}
                <Snackbar
                    open={!!errorText}
                    onClose={() => setErrorText('')}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    autoHideDuration={4000}
                >
                    <Alert severity="error" variant="filled" onClose={() => setErrorText('')}>
                        {errorText}
                    </Alert>
                </Snackbar>
            </StyledContainer>
        </Modal>
    );
};

const btnStyle = {
    background: '#304FFF',
    color: '#fff',
    gap: '6px',
    '&:hover': { background: '#1E40FF' }
};

export default ReceiptItemsModal;
